import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useEffect } from "react";
import { ScrollView } from "react-native";
import { useRefetchableFragment } from "react-relay";

import { CheckBox } from "@whoosh/design-system/src/components/controls";
import { Avatar } from "@whoosh/design-system/src/components/display";
import { HStack, Text } from "@whoosh/design-system/src/components/primitive";

import { AddPlayersClientsSearchQuery } from "./__generated__/AddPlayersClientsSearchQuery.graphql";
import { AddPlayers_Clients$key } from "./__generated__/AddPlayers_Clients.graphql";

export type Player = {
  id: string | null;
  firstName: string | null;
  lastName: string | null;
  clientType: "member" | "customer" | "guest" | "unknownCustomer";
  profileImagePublicId?: string | null;
  membershipTypeId?: string | null;
};

type AddPlayersProps = {
  onTogglePlayer: (isChecked: boolean, player: Player) => void;
  clubRef: AddPlayers_Clients$key | null;
  selectedPlayerIds: string[];
  excludedPlayerIds?: string[];
  isBookingFull: boolean;
  search?: string;
};

export const AddPlayers = ({
  onTogglePlayer,
  clubRef,
  selectedPlayerIds,
  excludedPlayerIds,
  isBookingFull = false,
  search = "",
}: AddPlayersProps) => {
  const exclusionIds = excludedPlayerIds ?? selectedPlayerIds;
  const [club, refetch] = useRefetchableFragment<
    AddPlayersClientsSearchQuery,
    AddPlayers_Clients$key
  >(AddPlayersFragment, clubRef);

  useEffect(() => {
    refetch({ search: search.trim(), players: exclusionIds });
  }, [exclusionIds, refetch, search]);

  return (
    <ScrollView>
      {club?.clients?.edges?.map((edge) => {
        const isChecked = !!selectedPlayerIds.find(
          (playerId) => playerId === edge?.node?.id
        );
        return (
          <HStack
            key={edge?.node?.id}
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            py={2}
          >
            <HStack alignItems="center" gap={2}>
              <Avatar
                profileImagePublicId={edge?.node?.profileImage?.publicId || ""}
                firstName={edge?.node?.firstName ?? null}
                lastName={edge?.node?.lastName ?? null}
                size={"sm"}
                type={"customer"}
              />
              <Text variant="mainBaseSemiBold">{edge?.node?.fullName}</Text>
            </HStack>
            <CheckBox
              aria-label={t`add item checkbox`}
              isChecked={isChecked}
              onPress={() =>
                onTogglePlayer(!isChecked, {
                  id: edge?.node?.id || "",
                  firstName: edge?.node?.firstName ?? null,
                  lastName: edge?.node?.lastName ?? null,
                  clientType: "customer",
                  profileImagePublicId:
                    edge?.node?.profileImage?.publicId || "",
                  membershipTypeId: edge?.node?.membershipType?.id || null,
                })
              }
              isDisabled={isBookingFull && !isChecked}
            />
          </HStack>
        );
      })}
    </ScrollView>
  );
};

export const AddPlayersFragment = graphql`
  fragment AddPlayers_Clients on Club
  @argumentDefinitions(search: { type: "String" }, players: { type: "[ID]" })
  @refetchable(queryName: "AddPlayersClientsSearchQuery") {
    clients(first: 25, active: true, players: $players, search: $search) {
      edges {
        node {
          membershipType {
            id
          }
          ... on Member {
            id
            fullName
            firstName
            lastName
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
          ... on Customer {
            id
            fullName
            firstName
            lastName
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
        }
      }
    }
  }
`;
